import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Table,
  message,
  Typography,
  Row,
  Col,
  Space,
} from "antd";
import { ArrowLeftOutlined, LinkOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { useAgentProfile } from "../../hooks/useAgentProfile";
import {
  fetchAgentContainerById,
  fetchContainerBookingsForAgent,
  updateAgentContainer,
} from "../../services/agentPortalService";
import {
  AGENT_CONTAINER_STATUSES,
  AGENT_ROUTES,
  mapContainerStatusToBookingStatus,
} from "../../config/agentPortal";
import { Booking, Container } from "../../types/booking";
import { getBookingEffReference } from "../../utils/bookingUtils";
import { AgentContainerProductsTable } from "../../components/agent/AgentContainerProductsTable";
import { ContainerMasterAwbBlReadOnly } from "../../components/containers/MasterContainerAwbBlSection";
import { AgentContainerTrackMap } from "../../components/agent/AgentContainerTrackMap";
import { AgentContainerTableSummary } from "../../components/agent/AgentContainerTableSummary";
import { AgentAddToMasterModal } from "../../components/agent/AgentAddToMasterModal";
import { AgentContainerExportDropdown } from "../../components/agent/AgentContainerExportDropdown";
import {
  AgentBookingStatusTag,
  renderAgentContainerStatusLabel,
  renderAgentContainerStatusOption,
} from "../../components/agent/agentStatusTags";
import {
  agentCompactCardStyles,
  agentCompactTableEvenClass,
  spreadColumnsEvenly,
} from "../../components/agent/agentCompactTable";
import {
  downloadAgentContainerBookings,
  downloadAgentContainerProducts,
} from "../../utils/agentContainerExport";
import { getLinkedMastersFromContainer } from "../../utils/agentContainerMasterUtils";

const { Title, Text, Link } = Typography;

const AgentContainerDetailPage: React.FC = () => {
  const { id } = useParams();
  const containerId = Number(id);
  const navigate = useNavigate();
  const { agentId, loading: profileLoading } = useAgentProfile();
  const [container, setContainer] = useState<Container | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [originPorts, setOriginPorts] = useState<{ id: number; name: string }[]>(
    []
  );
  const [destinationPorts, setDestinationPorts] = useState<
    { id: number; name: string }[]
  >([]);
  const [addToMasterOpen, setAddToMasterOpen] = useState(false);
  const trackAndTrace =
    Form.useWatch("track_and_trace", form) ?? container?.track_and_trace ?? "";

  const load = useCallback(async () => {
    if (!agentId || !Number.isFinite(containerId)) return;
    setLoading(true);
    const [{ data: c, error: cErr }, { data: b, error: bErr }] =
      await Promise.all([
        fetchAgentContainerById(containerId),
        fetchContainerBookingsForAgent(agentId, containerId),
      ]);
    if (cErr || !c) {
      message.error("Container not found");
      navigate(AGENT_ROUTES.containers);
      return;
    }
    if (bErr) message.error("Failed to load bookings");
    setContainer(c);
    setBookings(b);
    form.setFieldsValue({
      status: c.status,
      reference: c.reference,
      etd: c.etd ? dayjs(c.etd) : null,
      delayed_etd: c.delayed_etd ? dayjs(c.delayed_etd) : null,
      eta: c.eta ? dayjs(c.eta) : null,
      track_and_trace: c.track_and_trace,
      origin_port: c.origin_port,
      destination_port: c.destination_port,
    });
    setLoading(false);
  }, [agentId, containerId, form, navigate]);

  useEffect(() => {
    if (!profileLoading) load();
  }, [profileLoading, load]);

  useEffect(() => {
    void Promise.all([
      supabase
        .from("booking_origin_port")
        .select("id, name")
        .order("name")
        .then(({ data }) => setOriginPorts(data || [])),
      supabase
        .from("booking_destination_port")
        .select("id, name")
        .order("name")
        .then(({ data }) => setDestinationPorts(data || [])),
    ]);
  }, []);

  const handleSave = async () => {
    if (!container) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        status: values.status,
        reference: values.reference,
        etd: values.etd ? values.etd.format("YYYY-MM-DD") : null,
        delayed_etd: values.delayed_etd
          ? values.delayed_etd.format("YYYY-MM-DD")
          : null,
        eta: values.eta ? values.eta.format("YYYY-MM-DD") : null,
        track_and_trace: values.track_and_trace || null,
        origin_port: values.origin_port || null,
        destination_port: values.destination_port || null,
      };
      const { error } = await updateAgentContainer(container.id, payload);
      if (error) throw error;

      const bookingStatus = mapContainerStatusToBookingStatus(values.status);
      if (bookingStatus) {
        await supabase
          .from("bookings")
          .update({ status: bookingStatus })
          .eq("container_id", container.id)
          .eq("agent_id", agentId);
      }

      message.success("Container updated");
      load();
    } catch (err) {
      console.error(err);
      message.error("Failed to update container");
    } finally {
      setSaving(false);
    }
  };

  if (profileLoading || loading || !container) return null;

  const linkedMasters = getLinkedMastersFromContainer(container);

  const bookingColumns = spreadColumnsEvenly(
    [
      {
        title: "Booking",
        key: "booking_ref",
        ellipsis: false,
        render: (_: unknown, row: Booking) => getBookingEffReference(row),
      },
      {
        title: "Status",
        dataIndex: "status",
        ellipsis: false,
        render: (status: string) => (
          <AgentBookingStatusTag status={status} />
        ),
      },
      {
        title: "Incoterm",
        key: "incoterm",
        ellipsis: false,
        render: (_: unknown, row: Booking) => {
          const incoterm = Array.isArray(row.booking_incoterm_info)
            ? row.booking_incoterm_info[0]
            : row.booking_incoterm_info;
          return incoterm?.name || "-";
        },
      },
      {
        title: "Ready",
        dataIndex: "origin_pickup_date",
        render: (v: string) => (v ? dayjs(v).format("D/M") : "-"),
      },
      {
        title: "Pickup/drop off date",
        dataIndex: "confirmed_pick_up_date",
        render: (v: string) => (v ? dayjs(v).format("D/M") : "-"),
      },
      {
        title: "Export license",
        dataIndex: "export_clearance",
        render: (v: boolean) => (v ? "☑" : "-"),
      },
      {
        title: "Commodity inspection",
        dataIndex: "commodity_inspection",
        render: (v: boolean) => (v ? "☑" : "-"),
      },
      {
        title: "Post export license",
        dataIndex: "post_export_license",
        render: (v: number) => (v ? `$${v.toFixed(0)}` : "-"),
      },
      {
        title: "Pickup fee",
        dataIndex: "pick_up_fee",
        render: (v: number) => (v ? `$${v.toFixed(0)}` : "-"),
      },
    ],
    [1.6, 2, 1, 1, 1.5, 1.1, 1.1, 1.1, 1.1]
  );

  return (
    <div>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(AGENT_ROUTES.containers)}
        style={{ paddingLeft: 0, marginBottom: 8 }}
      >
        Back to containers
      </Button>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 8,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          {container.reference || `Container ${container.id}`}
        </Title>
        {agentId != null && (
          <Button
            icon={<LinkOutlined />}
            onClick={() => setAddToMasterOpen(true)}
          >
            Add to Master BL/AWB
          </Button>
        )}
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={6} lg={7}>
          <Card size="small" title="Edit consolidation">
            <Form form={form} layout="vertical" size="small">
              <Form.Item label="Status" name="status" style={{ marginBottom: 8 }}>
                <Select
                  optionRender={renderAgentContainerStatusOption}
                  labelRender={renderAgentContainerStatusLabel}
                  options={AGENT_CONTAINER_STATUSES.map((s) => ({
                    value: s.value,
                    label: s.label,
                  }))}
                />
              </Form.Item>
              <Form.Item
                label="Reference"
                name="reference"
                rules={[{ required: true }]}
                style={{ marginBottom: 8 }}
              >
                <Input />
              </Form.Item>
              <Form.Item label="B/L number" style={{ marginBottom: 8 }}>
                {linkedMasters.length > 0 ? (
                  <Space direction="vertical" size={0}>
                    {linkedMasters.map((master) => {
                      const label =
                        master.reference || `Master #${master.id}`;
                      const canManage =
                        agentId != null && master.agent_id === agentId;
                      return canManage ? (
                        <Link
                          key={master.id}
                          onClick={() =>
                            navigate(
                              `${AGENT_ROUTES.masterContainers}?master_id=${master.id}&action=manage`
                            )
                          }
                        >
                          {label}
                        </Link>
                      ) : (
                        <Text key={master.id} strong>
                          {label}
                        </Text>
                      );
                    })}
                  </Space>
                ) : (
                  <Space size={4} wrap>
                    <Text type="secondary">Not linked</Text>
                    {agentId != null && (
                      <Button
                        type="link"
                        size="small"
                        icon={<LinkOutlined />}
                        onClick={() => setAddToMasterOpen(true)}
                        style={{ paddingInline: 0 }}
                      >
                        Add to Master BL/AWB
                      </Button>
                    )}
                  </Space>
                )}
              </Form.Item>
              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item
                    label="ETD (original)"
                    name="etd"
                    tooltip="Original booked departure — used for invoicing"
                    style={{ marginBottom: 8 }}
                  >
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Delayed ETD"
                    name="delayed_etd"
                    tooltip="Optional operational delayed departure"
                    style={{ marginBottom: 8 }}
                  >
                    <DatePicker style={{ width: "100%" }} allowClear />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="ETA" name="eta" style={{ marginBottom: 8 }}>
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Track & trace" name="track_and_trace" style={{ marginBottom: 8 }}>
                <Input />
              </Form.Item>
              <AgentContainerTrackMap trackAndTrace={trackAndTrace} />
              <Form.Item label="Origin port" name="origin_port" style={{ marginBottom: 8 }}>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={originPorts.map((p) => ({
                    value: p.id,
                    label: p.name,
                  }))}
                />
              </Form.Item>
              <Form.Item label="Destination port" name="destination_port" style={{ marginBottom: 8 }}>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={destinationPorts.map((p) => ({
                    value: p.id,
                    label: p.name,
                  }))}
                />
              </Form.Item>
              <Button type="primary" block onClick={handleSave} loading={saving}>
                Save consolidation
              </Button>
            </Form>
          </Card>
          <ContainerMasterAwbBlReadOnly
            containerId={container.id}
            refreshToken={linkedMasters[0]?.id ?? null}
            style={{ marginTop: 12 }}
          />
        </Col>
        <Col xs={24} xl={18} lg={17}>
          <Card
            size="small"
            styles={agentCompactCardStyles}
            title={`Bookings in ${container.reference || "container"}`}
            extra={
              <AgentContainerExportDropdown
                disabled={bookings.length === 0}
                onExport={(format) =>
                  downloadAgentContainerBookings(container, bookings, format)
                }
              />
            }
          >
            <AgentContainerTableSummary
              items={[{ value: bookings.length, label: "bookings" }]}
            />
            <Table
              rowKey="id"
              size="small"
              className={agentCompactTableEvenClass}
              dataSource={bookings}
              pagination={false}
              tableLayout="fixed"
              onRow={(row) => ({
                onClick: () => navigate(AGENT_ROUTES.bookingDetail(row.id)),
                style: { cursor: "pointer" },
              })}
              columns={bookingColumns}
            />
          </Card>
          <div style={{ marginTop: 12 }}>
            <AgentContainerProductsTable
              bookings={bookings}
              containerReference={container.reference}
              loading={loading}
              compact
              onExport={(format) =>
                downloadAgentContainerProducts(container, bookings, format)
              }
            />
          </div>
        </Col>
      </Row>

      {agentId != null && (
        <AgentAddToMasterModal
          open={addToMasterOpen}
          agentId={agentId}
          containerId={container.id}
          containerReference={container.reference}
          transportTypeId={container.transport_type}
          transportTypeName={
            Array.isArray(container.transport_type_info)
              ? container.transport_type_info[0]?.name
              : container.transport_type_info?.name
          }
          onClose={() => setAddToMasterOpen(false)}
          onSuccess={() => {
            setAddToMasterOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
};

export default AgentContainerDetailPage;
